// R-SIS (Phase 3) — HelpNote: a short, dismissible in-page guide banner so
// users know where they are + what to do. Persists the dismissed state per
// `storageKey`; once closed it collapses to a small «راهنما» toggle. Non
// -intrusive, Persian, RTL. Used across the R-SIS flows.

import React from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "../icons";

interface HelpNoteProps {
  storageKey: string;
  title: string;
  children: React.ReactNode;
}

export const HelpNote: React.FC<HelpNoteProps> = ({ storageKey, title, children }) => {
  const { t } = useTranslation("common");
  const key = `help_dismissed_${storageKey}`;
  const [open, setOpen] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(key) !== "1";
    } catch {
      return true;
    }
  });

  const dismiss = () => {
    setOpen(false);
    try {
      localStorage.setItem(key, "1");
    } catch {
      /* ignore */
    }
  };
  const reopen = () => {
    setOpen(true);
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={reopen}
        className="btn btn-sm btn-outline"
        style={{ marginBottom: 12, display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        <Icon name="question" size={14} />
        {t("help.toggle")}
      </button>
    );
  }

  return (
    <div
      className="card"
      role="note"
      style={{
        padding: "12px 14px",
        marginBottom: 16,
        background: "color-mix(in oklch, var(--accent) 8%, var(--surface))",
        borderInlineStart: "3px solid var(--accent)",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
      }}
    >
      <span style={{ color: "var(--accent)", flexShrink: 0, marginTop: 2 }}>
        <Icon name="question" size={18} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, lineHeight: 1.8, color: "var(--fg-mute)" }}>{children}</div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t("help.close")}
        className="btn-icon"
        style={{ flexShrink: 0 }}
      >
        <Icon name="x" size={16} />
      </button>
    </div>
  );
};

export default HelpNote;
